"use client";

import React, { useEffect, useId, useRef, useState } from "react";
import { Loader2, ZoomIn, ZoomOut, RotateCcw } from "lucide-react";

interface MermaidRendererProps {
  code: string;
  accentColor?: string;
  className?: string;
}

export function MermaidRenderer({ code, accentColor = "#38bdf8", className }: MermaidRendererProps) {
  const [svgContent, setSvgContent] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [zoom, setZoom] = useState<number>(1);
  const rawId = useId();
  const id = "mermaid-" + rawId.replace(/[^a-zA-Z0-9_-]/g, "");
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let isMounted = true;

    async function renderDiagram() {
      try {
        const mermaid = (await import("mermaid")).default;

        mermaid.initialize({
          startOnLoad: false,
          theme: "dark",
          securityLevel: "loose",
          fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
          themeVariables: {
            darkMode: true,
            background: "transparent",
            mainBkg: "#09090b",
            primaryColor: "#09090b",
            primaryTextColor: "#f4f4f5",
            primaryBorderColor: accentColor,
            lineColor: "#71717a",
            secondaryColor: "#18181b",
            tertiaryColor: "#09090b",
            nodeBorder: "#3f3f46",
            clusterBkg: "#000000",
            clusterBorder: "#27272a",
            titleColor: "#fafafa",
            edgeLabelBackground: "#18181b",
            actorTextColor: "#f4f4f5",
            actorBkg: "#09090b",
            actorBorder: accentColor,
            signalColor: accentColor,
            signalTextColor: "#f4f4f5",
            nodeTextColor: "#f4f4f5",
          },
          flowchart: {
            useMaxWidth: false,
            htmlLabels: true,
            curve: "basis",
          },
          sequence: {
            useMaxWidth: false,
            wrap: true,
          },
        });

        // Clean code string
        const cleanCode = code.trim();
        const { svg } = await mermaid.render(id, cleanCode);

        if (isMounted) {
          // Enhance SVG with responsive styles
          const styledSvg = svg
            .replace(/<svg\s+([^>]+)>/, '<svg class="w-full h-auto max-w-full" $1>')
            .replace(/style="max-width:[^"]*"/g, "");

          setSvgContent(styledSvg);
          setError(null);
        }
      } catch (err: any) {
        console.error("Mermaid render error:", err);
        if (isMounted) {
          setError(err?.message || "Failed to render architecture diagram.");
        }
      }
    }

    renderDiagram();

    return () => {
      isMounted = false;
    };
  }, [code, id, accentColor]);

  const handleZoomIn = () => setZoom((z) => Math.min(z + 0.15, 1.6));
  const handleZoomOut = () => setZoom((z) => Math.max(z - 0.15, 0.7));
  const handleResetZoom = () => setZoom(1);

  if (error) {
    return (
      <div className="p-6 rounded-2xl bg-red-500/10 border border-red-500/20 text-red-300 font-mono text-xs overflow-x-auto">
        <p className="font-bold mb-2">Diagram Rendering Notice:</p>
        <pre className="whitespace-pre-wrap">{code}</pre>
      </div>
    );
  }

  return (
    <div className="relative w-full overflow-hidden rounded-3xl bg-black border border-white/10 group">
      
      {/* Zoom Controls Overlay */}
      <div className="absolute top-4 right-4 z-20 flex items-center gap-1.5 p-1 rounded-xl bg-zinc-900/80 backdrop-blur-md border border-white/10 opacity-0 group-hover:opacity-100 transition-opacity">
        <button
          onClick={handleZoomIn}
          title="Zoom In"
          className="p-1.5 rounded-lg hover:bg-white/10 text-zinc-400 hover:text-white transition-colors"
        >
          <ZoomIn className="w-3.5 h-3.5" />
        </button>
        <button
          onClick={handleZoomOut}
          title="Zoom Out"
          className="p-1.5 rounded-lg hover:bg-white/10 text-zinc-400 hover:text-white transition-colors"
        >
          <ZoomOut className="w-3.5 h-3.5" />
        </button>
        <button
          onClick={handleResetZoom}
          title="Reset Zoom"
          className="p-1.5 rounded-lg hover:bg-white/10 text-zinc-400 hover:text-white transition-colors"
        >
          <RotateCcw className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* SVG Canvas Container with Smooth Pan/Zoom */}
      <div
        ref={containerRef}
        className="w-full min-h-[320px] sm:min-h-[420px] p-6 sm:p-10 flex items-center justify-center overflow-x-auto scrollbar-thin scrollbar-thumb-white/10"
      >
        {svgContent ? (
          <div
            style={{ transform: `scale(${zoom})`, transformOrigin: "center center" }}
            className="w-full flex justify-center transition-transform duration-200"
            dangerouslySetInnerHTML={{ __html: svgContent }}
          />
        ) : (
          <div className="flex flex-col items-center justify-center gap-3 py-16 text-zinc-500 font-mono text-xs">
            <Loader2 className="w-5 h-5 animate-spin" style={{ color: accentColor }} />
            <span>Compiling runtime graph schematic...</span>
          </div>
        )}
      </div>

    </div>
  );
}
